/**
 * enumLabels.ts — Issue #228
 *
 * 跨模块共享的中文 label map。
 *
 * 设计原则：
 * - 仅替换前端可见文案；不修改后端 OpenAPI / 数据库枚举值。
 * - 未识别值返回受控 fallback（如 `未识别状态`），不再直接渲染英文枚举。
 * - 纯数据 + 函数模块，不引入 React / api-client 运行时依赖，可在 Vitest 直接 import。
 */

export function formatEnumLabel<T extends string>(
  value: T | string | null | undefined,
  labels: Readonly<Record<string, string>>,
  fallback = '未识别',
): string {
  if (value == null || value === '') {
    return fallback;
  }
  return labels[value] ?? fallback;
}

// ─── 通用风险等级 ────────────────────────────────────────────────────────────

export type RiskLevel = 'low' | 'medium' | 'high' | 'critical';

export const RISK_LEVEL_LABELS: Readonly<Record<RiskLevel, string>> = {
  low: '低',
  medium: '中',
  high: '高',
  critical: '极高',
};

export const RISK_LEVEL_LIST: readonly RiskLevel[] = ['low', 'medium', 'high', 'critical'];

// ─── 合规：政策文件状态 ──────────────────────────────────────────────────────

export type PolicyFileStatus = 'draft' | 'active' | 'superseded' | 'expired' | 'archived';

export const POLICY_FILE_STATUS_LABELS: Readonly<Record<PolicyFileStatus, string>> = {
  draft: '草稿',
  active: '生效中',
  superseded: '已被替代',
  expired: '已过期',
  archived: '已归档',
};

// ─── 合规：项目合规状态 ──────────────────────────────────────────────────────

export type ProjectComplianceStatus =
  | 'internal_research'
  | 'serviceable'
  | 'paused'
  | 'forbidden_sale'
  | 'special_approval_only'
  | 'expert_review_only';

export const PROJECT_COMPLIANCE_STATUS_LABELS: Readonly<Record<ProjectComplianceStatus, string>> = {
  internal_research: '内部研究',
  serviceable: '可售',
  paused: '已暂停',
  forbidden_sale: '禁售',
  special_approval_only: '仅特批',
  expert_review_only: '仅专家会诊',
};

// ─── 合规：宣传材料类型/状态/审核 ────────────────────────────────────────────

export type PromotionMaterialType =
  | 'ppt'
  | 'social_copy'
  | 'case'
  | 'video'
  | 'testimonial'
  | 'proposal_wording'
  | 'project_intro';

export const PROMOTION_MATERIAL_TYPE_LABELS: Readonly<
  Record<PromotionMaterialType, string>
> = {
  ppt: '宣讲 PPT',
  social_copy: '社交文案',
  case: '案例分享',
  video: '视频素材',
  testimonial: '客户证言',
  proposal_wording: '方案话术',
  project_intro: '项目介绍',
};

export type PromotionMaterialStatus =
  | 'draft'
  | 'submitted'
  | 'under_review'
  | 'changes_requested'
  | 'approved'
  | 'rejected'
  | 'withdrawn'
  | 'archived';

export const PROMOTION_MATERIAL_STATUS_LABELS: Readonly<
  Record<PromotionMaterialStatus, string>
> = {
  draft: '草稿',
  submitted: '已提交',
  under_review: '审核中',
  changes_requested: '需修改',
  approved: '已批准',
  rejected: '已驳回',
  withdrawn: '已撤回',
  archived: '已归档',
};

export type PromotionReviewResult = 'approved' | 'rejected' | 'changes_requested';

export const PROMOTION_REVIEW_RESULT_LABELS: Readonly<
  Record<PromotionReviewResult, string>
> = {
  approved: '通过',
  rejected: '驳回',
  changes_requested: '需修改',
};

export type PromotionReviewerRole = 'compliance' | 'medical' | 'legal';

export const PROMOTION_REVIEWER_ROLE_LABELS: Readonly<
  Record<PromotionReviewerRole, string>
> = {
  compliance: '合规',
  medical: '医学',
  legal: '法务',
};

// ─── 合规：敏感词库 ──────────────────────────────────────────────────────────

export type SensitiveTermCategory = 'forbidden' | 'risky' | 'regulated';

export const SENSITIVE_TERM_CATEGORY_LABELS: Readonly<
  Record<SensitiveTermCategory, string>
> = {
  forbidden: '禁用词',
  risky: '风险词',
  regulated: '受监管词',
};

export type SensitiveTermStatus = 'active' | 'inactive' | 'archived';

export const SENSITIVE_TERM_STATUS_LABELS: Readonly<
  Record<SensitiveTermStatus, string>
> = {
  active: '启用',
  inactive: '停用',
  archived: '已归档',
};

// ─── 合规：复核任务 ──────────────────────────────────────────────────────────

export type ComplianceRecheckStatus =
  | 'pending'
  | 'in_progress'
  | 'resolved'
  | 'cancelled';

export const COMPLIANCE_RECHECK_STATUS_LABELS: Readonly<
  Record<ComplianceRecheckStatus, string>
> = {
  pending: '待处理',
  in_progress: '处理中',
  resolved: '已结案',
  cancelled: '已取消',
};

// ─── 任务中心：状态/优先级/来源 ──────────────────────────────────────────────

export type TaskCenterStatus =
  | 'pending'
  | 'in_progress'
  | 'deferred'
  | 'completed'
  | 'canceled';

export const TASK_CENTER_STATUS_LABELS: Readonly<Record<TaskCenterStatus, string>> = {
  pending: '待处理',
  in_progress: '处理中',
  deferred: '已延期',
  completed: '已完成',
  canceled: '已取消',
};

export type TaskCenterPriority =
  | 'low'
  | 'normal'
  | 'high'
  | 'urgent'
  | 'medical_safety';

export const TASK_CENTER_PRIORITY_LABELS: Readonly<
  Record<TaskCenterPriority, string>
> = {
  low: '低',
  normal: '普通',
  high: '高',
  urgent: '紧急',
  medical_safety: '医疗安全',
};

export type TaskCenterSource =
  | 'crm_stage_followup'
  | 'proposal_review'
  | 'payment_confirmation'
  | 'project_unlock_approval'
  | 'lab_schedule'
  | 'sample_receive_confirmation'
  | 'qc_review'
  | 'infusion_identity_check'
  | 'followup_reminder'
  | 'inventory_warning'
  | 'compliance_recheck'
  | 'promotion_review'
  | 'manual'
  | 'finance'
  | 'scheduling'
  | 'operations'
  | 'lab';

export const TASK_CENTER_SOURCE_LABELS: Readonly<Record<TaskCenterSource, string>> = {
  crm_stage_followup: 'CRM 阶段跟进',
  proposal_review: '方案审核',
  payment_confirmation: '付款确认',
  project_unlock_approval: '项目解锁审批',
  lab_schedule: '实验室排期',
  sample_receive_confirmation: '样本接收确认',
  qc_review: '质检复核',
  infusion_identity_check: '回输身份核对',
  followup_reminder: '随访提醒',
  inventory_warning: '库存预警',
  compliance_recheck: '合规复核',
  promotion_review: '宣传审核',
  manual: '手动创建',
  finance: '财务',
  scheduling: '排班',
  operations: '运营',
  lab: '实验室',
};

// ─── CRM 订单项目状态 ───────────────────────────────────────────────────────
//
// 后端 `CrmOrderProjectDto.status` 当前是 `string`，仍可能出现新枚举值。
// 这里只为已知字符串提供中文 label，未知值由 fallback 处理。

export const CRM_ORDER_PROJECT_STATUS_LABELS: Readonly<Record<string, string>> = {
  pending: '待启动',
  planning: '规划中',
  awaiting_unlock: '待解锁',
  locked: '已锁定',
  unlocked: '已解锁',
  scheduled: '已排期',
  in_progress: '进行中',
  delivering: '交付中',
  delivered: '已交付',
  completed: '已完成',
  closed: '已关闭',
  cancelled: '已取消',
  canceled: '已取消',
  on_hold: '已挂起',
};

// ─── 库存物料分类 ───────────────────────────────────────────────────────────
//
// 后端 `ConsumableResponseDto.category` 为开放字符串（未约束枚举）。
// 这里提供常见分类的中文映射；未知值由调用方通过 `formatEnumLabel`
// 走受控中文 fallback（如 `未知分类`），不再直接渲染原始英文枚举文本。

// Issue #314: 8 类受控分类（试剂、耗材、药品设备、防护用品、冷链物资、细胞培养、包装材料、其他）。
// `drug_device` 合并原先拆分的 `drug` / `device`，统一在前端展示为 `药品设备`，
// 后端入库时仍按 `varchar` 存储（无数据库 ENUM，AI GUIDE §2 红线）。
//
// 红线：新增耗材下拉只暴露这 8 个 value（见 `INVENTORY_CATEGORY_LABELS` 的
// `Object.keys` 长度断言），不在创建路径上回收 `drug` / `device` 等历史 value。
export const INVENTORY_CATEGORY_LABELS: Readonly<Record<string, string>> = {
  reagent: '试剂',
  consumable: '耗材',
  drug_device: '药品设备',
  ppe: '防护用品',
  cold_chain: '冷链物资',
  cell_culture: '细胞培养',
  packaging: '包装材料',
  other: '其他',
};

// Legacy display aliases — 老数据中仍可能存在 `category='drug'` 或
// `category='device'`，应在列表中统一展示为「药品设备」而不是兜底「未知分类」。
// 仅用于展示侧 normalize；不暴露给创建表单，避免新数据写回老 value。
const LEGACY_INVENTORY_CATEGORY_ALIASES: Readonly<Record<string, string>> = {
  drug: 'drug_device',
  device: 'drug_device',
};

/**
 * 将外部传入的库存物料分类值规范化为 `INVENTORY_CATEGORY_LABELS` 的受控 key：
 * - 老数据 `drug` / `device` -> `drug_device`。
 * - 其它已知 key 原样返回（含未识别值，由调用方走中文兜底）。
 * - `null` / `undefined` / 空字符串保持原样，让 `formatEnumLabel` 命中其受控兜底。
 */
export function normalizeInventoryCategoryValue<T extends string | null | undefined>(
  value: T,
): T | string {
  if (value == null || value === '') return value;
  const alias = LEGACY_INVENTORY_CATEGORY_ALIASES[value];
  return alias ?? value;
}

/**
 * 库存物料分类专用的中文格式化助手：先把老 value 归一化为受控 key，
 * 再走共享的 `formatEnumLabel` 中文兜底。默认兜底文案保持 `未知分类`。
 */
export function formatInventoryCategoryLabel(
  value: string | null | undefined,
  fallback = '未知分类',
): string {
  return formatEnumLabel(normalizeInventoryCategoryValue(value), INVENTORY_CATEGORY_LABELS, fallback);
}

// ─── AI 模型中心 ────────────────────────────────────────────────────────────

export type LlmProviderType =
  | 'official_cloud'
  | 'proxy_aggregator'
  | 'local_private'
  | 'custom';

export const LLM_PROVIDER_TYPE_LABELS: Readonly<Record<LlmProviderType, string>> = {
  official_cloud: '官方云 API',
  proxy_aggregator: '代理聚合',
  local_private: '本地私有',
  custom: '自定义',
};

export type LlmAuthMode =
  | 'bearer_token'
  | 'api_key_header'
  | 'custom_header'
  | 'none';

export const LLM_AUTH_MODE_LABELS: Readonly<Record<LlmAuthMode, string>> = {
  bearer_token: 'Bearer Token',
  api_key_header: 'API Key Header',
  custom_header: '自定义请求头',
  none: '无（仅本地私有）',
};

export type LlmDataResidency = 'domestic' | 'cross_border' | 'local_private';

export const LLM_DATA_RESIDENCY_LABELS: Readonly<Record<LlmDataResidency, string>> = {
  domestic: '境内',
  cross_border: '跨境',
  local_private: '本地私有',
};

export type LlmCredentialScope = 'shared' | 'internal_only' | 'user_only';

export const LLM_CREDENTIAL_SCOPE_LABELS: Readonly<
  Record<LlmCredentialScope, string>
> = {
  shared: '共享',
  internal_only: '仅内部使用',
  user_only: '仅创建者',
};

export const LLM_MODEL_SOURCE_LABELS: Readonly<Record<string, string>> = {
  manual: '手工录入',
  provider_sync: '平台同步',
  imported: '已导入',
};

export const LLM_CALL_STATUS_LABELS: Readonly<Record<string, string>> = {
  succeeded: '成功',
  failed: '失败',
  cancelled: '已取消',
  pending: '处理中',
  timeout: '已超时',
  rate_limited: '被限流',
};

export const LLM_TRIGGER_TYPE_LABELS: Readonly<Record<string, string>> = {
  manual: '手动触发',
  automation: '自动化任务',
  scheduled: '定时任务',
  background_job: '后台作业',
  customer_visible: '客户可见调用',
};

export const LLM_REVIEW_STATUS_LABELS: Readonly<Record<string, string>> = {
  pending: '待审核',
  approved: '已审核通过',
  rejected: '已驳回',
  not_required: '无需审核',
};

// ─── 短信模板 / 发送日志 ──────────────────────────────────────────────────────

export const SMS_SCENE_LABELS: Readonly<Record<string, string>> = {
  admin_login: '后台登录',
  admin_sensitive: '后台敏感操作',
  admin_password_reset: '后台密码重置',
  customer_login: '客户登录',
  customer_bind_phone: '客户绑定手机号',
  customer_password_reset: '客户密码重置',
  high_risk_login_step_up: '高风险登录加固',
};

export const SMS_TEMPLATE_STATUS_LABELS: Readonly<Record<string, string>> = {
  draft: '草稿',
  active: '启用',
  disabled: '禁用',
  archived: '已归档',
};

export const SMS_SEND_RESULT_LABELS: Readonly<Record<string, string>> = {
  queued: '已入队',
  sent: '已发送',
  delivered: '已送达',
  failed: '失败',
  rate_limited: '被限流',
  rejected: '前置拒绝',
};
